import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";

import PAGE_SEO from "../data/pageSeo.json";
import CATEGORY_SEO from "../data/categorySeo.json";
import ARTICLES from "../data/articles.json";
import { toSlug } from "../utils/categorySlug";
import { useCurrency } from "../services/contexts/CurrencyContext";
import { Sparkle, Moon, Cloud, Heart, Footprints, Wave, Hills } from "./Doodles";
import { ChristmasBanner } from "./ChristmasBanner";
import { ProductImage } from "./ProductImage";
import { cdnImage } from "../utils/cdnImage";
import "../styles/home.css";

/* Иконите са вградени SVG вместо Font Awesome: рисуват се веднага, наследяват
   currentColor и не зависят от външен CDN. */
const Icon = ({ path, size = 20, fill = false }) => (
  <svg
    className="sa-icon"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    fill={fill ? "currentColor" : "none"}
    stroke={fill ? "none" : "currentColor"}
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {path}
  </svg>
);

const ICON_CHAT = <path d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4 20l1.3-4.2A7.5 7.5 0 1 1 20 12Z" />;
const ICON_PLAY = <path d="M8 5.5v13l11-6.5-11-6.5Z" />;

const openChat = () => {
  if (window.Tawk_API && window.Tawk_API.maximize) window.Tawk_API.maximize();
};

/* ── Томбола ──────────────────────────────────────────────────────────────
   Update GIVEAWAY_END date to reactivate. */
const GIVEAWAY_END = new Date("2026-05-25T23:59:59");

function useCountdown(endDate) {
  const calc = () => {
    const diff = endDate - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return time;
}

function GiveawayBanner() {
  const { days, hours, minutes, seconds } = useCountdown(GIVEAWAY_END);
  if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) return null;
  return (
    <div className="giveaway">
      <div className="giveaway-inner">
        <div>
          <p className="giveaway-title">🎁 Томбола — спечели ръчно рисувана тениска!</p>
          <p className="giveaway-sub">Следи страницата ни във Facebook и TikTok за участие</p>
        </div>
        <div className="giveaway-clock">
          {[['дни', days], ['часа', hours], ['мин', minutes], ['сек', seconds]].map(([label, val]) => (
            <div key={label} className="giveaway-unit">
              <span className="giveaway-num">{String(val).padStart(2, '0')}</span>
              <span className="giveaway-label">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── „За всеки вълшебен миг“ ──────────────────────────────────────────────
   Всяко плитче води към категория, която вече съществува — заглавията са
   h1-ите от src/data/categorySeo.json, за да не се разминава с страницата
   отсреща. Ръкописната бележка е само за настроение. */
const OCCASIONS = [
  { slug: 'patechki',   note: 'първите стъпки',  tone: 'mint',   doodle: 'footprints' },
  { slug: 'mesechinki', note: 'месец след месец', tone: 'lav',    doodle: 'moon' },
  { slug: 'detsa',      note: 'за най-малките',  tone: 'butter', doodle: 'heart' },
  { slug: 'semeystvo',  note: 'еднакви за всички', tone: 'peach', doodle: 'cloud' },
  { slug: 'koleda',     note: 'под елхата',      tone: 'sky',    doodle: 'sparkle' },
  { slug: 'chashi',     note: 'за всяко утро',   tone: 'rose',   doodle: 'sparkle' },
];

const OccasionDoodle = ({ kind }) => {
  if (kind === 'footprints') return <Footprints size={40} color="var(--sa-mint-300)" />;
  if (kind === 'moon') return <Moon size={40} color="var(--sa-butter-300)" />;
  if (kind === 'heart') return <Heart size={34} color="var(--sa-rose-400)" />;
  if (kind === 'cloud') return <Cloud width={54} color="#fff" />;
  return <Sparkle size={34} color="var(--sa-butter-500)" />;
};

/* ── „Как се ражда една приказка“ ─────────────────────────────────────────
   Само неща, които сайтът вече твърди: рисунка по избор, ръчна изработка в
   Пазарджик, доставка с Еконт и Спиди. Без срокове и цени. */
const STEPS = [
  {
    n: '1',
    title: 'Избираш рисунка или идея',
    text: 'Любим герой, снимка, име или нещо съвсем ваше — изпрати ни го в чата.',
  },
  {
    n: '2',
    title: 'Рисуваме я на ръка',
    text: 'Рисуваните дрехи се правят поотделно в ателието в Пазарджик, затова няма две еднакви. Щампите, чашите, картичките и коледните топки печатаме по ваша снимка или картинка.',
  },
  {
    n: '3',
    title: 'Пътува до вас',
    text: 'Изпращаме с Еконт или Спиди до цяла България.',
  },
];

/* Статиите за майки — ключовете са от src/data/articles.json */
const MOM_ARTICLES = ['kak-se-pravi-proshtapulnik', 'mesechinki-na-bebe', 'parvi-rozhden-den-na-bebe'];

/* Снимките в хероя: продукти за деца и за празниците водят, защото това търсят
   майките. Подреждаме по тежест, а не филтрираме — ако ателието е качило само
   чаши, героят пак има три рамки. */
const HERO_WEIGHTS = [
  [/пътечк|прощъпулн/i, 5],
  [/месечин/i, 5],
  [/деца|детск|боди|бебе/i, 4],
  [/колед|елх/i, 3],
  [/семе|комплект/i, 2],
];

/* Лентата „От работната маса“: по едно нещо от различни категории, за да се
   види широчината на ателието. Пропуска вече показаните в хероя, иначе същите
   три снимки излизат два пъти на една страница. */
function pickBandProducts(products, exclude, count = 5) {
  const used = new Set(exclude.map(p => p.id));
  const seenTypes = new Set();
  const picked = [];
  // първо по един от всяка категория, после каквото остане
  for (const pass of [true, false]) {
    for (const p of products) {
      if (picked.length >= count) break;
      if (!p.img || used.has(p.id)) continue;
      if (pass && seenTypes.has(p.type)) continue;
      used.add(p.id);
      seenTypes.add(p.type);
      picked.push(p);
    }
  }
  return picked;
}

const shuffle = list => {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const textOf = p => `${p.type || ''} ${p.name || ''}`;

/* Всеки продукт попада в една група, по реда тук: пътечка „за бебе Мики“ е
   пътечка, а не детска дреха, и платно за месечинки на бебе е месечинки. */
const heroGroup = p => {
  const t = textOf(p);
  if (/пътечк|прощъпулн/i.test(t)) return 'runner';
  if (/месечин/i.test(t)) return 'moon';
  if (/деца|детск|боди|бебе/i.test(t)) return 'kids';
  return null;
};

/* Рамките в реда на хероя. Голямата арка е висока и тясна — точно като
   пътечка; двете малки са детска дреха и платно за месечинки. Така всяко
   посещение показва по едно от трите неща, за които идват майките. */
const HERO_SLOTS = ['runner', 'kids', 'moon'];

/* При всяко посещение героят показва други снимки — случайна от всяка група.
   Липсва ли група, мястото се попълва по тежестите в HERO_WEIGHTS. */
function pickHeroProducts(products) {
  const withImg = products.filter(p => p.img);
  const picked = [];

  for (const group of HERO_SLOTS) {
    const p = shuffle(withImg.filter(x => heroGroup(x) === group))[0];
    if (p) picked.push(p);
  }

  const score = p => HERO_WEIGHTS.reduce((sum, [re, w]) => (re.test(textOf(p)) ? sum + w : sum), 0);
  const rest = withImg
    .filter(p => !picked.includes(p))
    .map((p, i) => ({ p, i, s: score(p) }))
    .sort((a, b) => b.s - a.s || a.i - b.i);
  for (const { p } of rest) {
    if (picked.length >= 3) break;
    picked.push(p);
  }
  return picked;
}

export const Home = ({ products }) => {
  const { formatPrice } = useCurrency();

  // Categories from products
  let categories = [...new Set(products.map(p => p.type))]
    .filter(Boolean)
    .filter(c => c !== 'Коледни топки за елха' && c !== 'топки за елха')
    .sort();

  // Коледните категории отиват най-отпред: първо „коледа“, после украсата.
  for (const xmas of ['Коледна украса', 'коледа']) {
    const i = categories.indexOf(xmas);
    if (i > -1) {
      categories.splice(i, 1);
      categories.unshift(xmas);
    }
  }

  // Брой продукти на категория — малка цифра, която показва колко има вътре.
  const countByType = products.reduce((acc, p) => {
    if (p.type) acc[p.type] = (acc[p.type] || 0) + 1;
    return acc;
  }, {});

  // Същите бройки, но по слъг — плочките с поводите сочат към слъговете от
  // categorySeo.json, а продуктите носят типа на кирилица.
  const countBySlug = Object.entries(countByType).reduce((acc, [type, n]) => {
    const slug = toSlug(type);
    acc[slug] = (acc[slug] || 0) + n;
    return acc;
  }, {});

  // Latest 9 products for the mini-grid
  const latest = [...products].slice(0, 9);

  // Products with videos
  const withVideos = products.filter(p => p.video).slice(0, 6);

  // Kids & baby products
  const kidsProducts = products.filter(p => {
    const t = (p.type || '').toLowerCase();
    const n = (p.name || '').toLowerCase();
    return t.includes('деца') || t.includes('бодит') || t.includes('месечинк') ||
           t.includes('пътечк') || n.includes('бебе') || n.includes('бодит') ||
           n.includes('прощъпулник') || n.includes('месечинк');
  }).slice(0, 4);

  // Изборът е случаен, затова се прави веднъж — щом има продукти — и се пази.
  // Страницата тръгва с вградения списък, а секунда по-късно Firestore го
  // подменя със същите продукти; нов избор тогава би сменил снимките пред
  // очите на посетителя (и Google би отчел втората като ново LCP). Пази ги и
  // при смяна € → лв.
  const heroRef = useRef(null);
  if (!heroRef.current && products.length) heroRef.current = pickHeroProducts(products);
  const heroProducts = heroRef.current || [];
  const heroFrames = [0, 1, 2].map(i => heroProducts[i] || null);
  const bandRef = useRef(null);
  if (!bandRef.current && products.length) bandRef.current = pickBandProducts(products, heroProducts);
  const bandProducts = bandRef.current || [];

  const label = cat => cat.charAt(0).toUpperCase() + cat.slice(1);

  return (
    <>
      <Helmet>
        <title>{PAGE_SEO.home.title}</title>
        <meta name="description" content={PAGE_SEO.home.description} />
        <link rel="canonical" href="https://stamsyart.bg/" />
      </Helmet>

      {/* ── Корица на книжка ── */}
      <section className="hero" aria-label="Stamsy Art">
        {/* Пастелните петна и небето зад текста */}
        <span className="hero-blob hero-blob--lav" aria-hidden="true" />
        <span className="hero-blob hero-blob--butter" aria-hidden="true" />
        <Cloud width={150} className="sa-doodle hero-cloud hero-cloud--1 sa-float sa-float--slow" />
        <Cloud width={104} className="sa-doodle hero-cloud hero-cloud--2 sa-float" />
        <Sparkle size={20} className="sa-doodle hero-star hero-star--1 sa-twinkle" />
        <Sparkle size={14} className="sa-doodle hero-star hero-star--2 sa-twinkle" color="var(--sa-rose-300)" />
        <Sparkle size={11} className="sa-doodle hero-star hero-star--3 sa-twinkle" color="var(--sa-lav-200)" />
        <Sparkle size={16} className="sa-doodle hero-star hero-star--4 sa-twinkle" />
        <Hills className="hero-hills" />

        <div className="hero-inner sa-wrap">
          <div className="hero-copy">
            <p className="sa-script hero-kicker">Рисувано на ръка, специално за вас</p>
            <h1 className="hero-title">Дрешки, които разказват приказки</h1>
            <p className="hero-lede">
              Бодита, тениски, пътечки за прощъпулник и месечинки — рисувани на ръка
              в ателието ни в Пазарджик, за малките и за цялото семейство.
            </p>
            <div className="hero-actions">
              <Link to="/catalog" className="sa-btn sa-btn--primary">Разгледай каталога</Link>
              <button type="button" className="sa-btn sa-btn--soft" onClick={openChat}>
                <Icon path={ICON_CHAT} size={18} />
                Пиши ни
              </button>
            </div>
          </div>

          {/* Три рамки-прозорчета. Мястото им е запазено с aspect-ratio, затова
              страницата не подскача, докато снимките се зареждат. */}
          <div className="hero-art">
            <Moon size={62} className="sa-doodle hero-moon sa-float" />
            <Sparkle size={26} className="sa-doodle hero-art-star sa-twinkle" />
            {heroFrames.map((product, i) => (
              <div key={i} className={`hero-frame hero-frame--${i + 1}`}>
                {product ? (
                  <Link to={`/catalog/${product.id}`} className="hero-frame-link">
                    {/* Първата снимка е най-голямото нещо на първия екран (LCP за
                        Google), затова се тегли веднага и с предимство. */}
                    <ProductImage
                      src={product.img}
                      alt={product.name || ''}
                      ratio={i === 0 ? 1.5 : 1}
                      widths={i === 0 ? [320, 640] : [240, 480]}
                      sizes={i === 0 ? '(min-width: 900px) 300px, 54vw' : '(min-width: 900px) 230px, 40vw'}
                      loading={i === 0 ? 'eager' : 'lazy'}
                      fetchpriority={i === 0 ? 'high' : undefined}
                      decoding="async"
                    />
                    <span className="hero-frame-name">{product.name}</span>
                  </Link>
                ) : (
                  <span className="hero-frame-skeleton" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>

        <Wave color="var(--sa-paper)" />
      </section>

      <GiveawayBanner />
      <ChristmasBanner />

      {/* ── За всеки вълшебен миг ── */}
      <section className="home-band home-band--pattern occasions">
        <div className="sa-wrap">
          <div className="sa-section-head sa-section-head--center reveal-up">
            <span className="sa-section-kicker">за всеки вълшебен миг</span>
            <h2 className="sa-section-title">Какъв е поводът?</h2>
          </div>

          <ul className="occ-grid">
            {OCCASIONS.map(({ slug, note, tone, doodle }, i) => (
              <li key={slug} className="reveal-up" style={{ transitionDelay: `${(i % 3) * 0.08}s` }}>
                <Link to={`/categories/${slug}`} className={`occ-tile occ-tile--${tone}`}>
                  <span className="occ-doodle" aria-hidden="true"><OccasionDoodle kind={doodle} /></span>
                  <span className="occ-note">{note}</span>
                  <span className="occ-name">{CATEGORY_SEO[slug].h1}</span>
                  {countBySlug[slug] > 0 && (
                    <span className="occ-count">
                      {countBySlug[slug]} {countBySlug[slug] === 1 ? 'продукт' : 'продукта'}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Всички категории ── */}
      {categories.length > 0 && (
        <section className="home-band cats">
          <div className="sa-wrap">
            <div className="sa-section-head cats-head reveal-up">
              <div>
                <span className="sa-section-kicker">разгледай по вид</span>
                <h2 className="sa-section-title">Всички категории</h2>
              </div>
              <Link to="/categories" className="sa-section-link">Виж всички категории →</Link>
            </div>
            <nav className="cat-chips" aria-label="Категории">
              {categories.map(cat => (
                <Link key={cat} className="sa-chip cat-chip" to={`/categories/${toSlug(cat)}`}>
                  {label(cat)}
                  {countByType[cat] > 0 && <span className="cat-chip-count">{countByType[cat]}</span>}
                </Link>
              ))}
            </nav>
          </div>
        </section>
      )}

      {/* ── Нови продукти ── */}
      {latest.length > 0 && (
        <section className="home-band">
          <div className="sa-wrap">
            <div className="sa-section-head reveal-up">
              <div>
                <span className="sa-section-kicker">току-що от ателието</span>
                <h2 className="sa-section-title">Нови продукти</h2>
              </div>
              <Link to="/catalog" className="sa-section-link">Виж всички →</Link>
            </div>
            <div className="home-products-grid">
              {latest.map((product, idx) => (
                <Link
                  key={product.id}
                  to={`/catalog/${product.id}`}
                  className="sa-card reveal-up"
                  style={{ transitionDelay: `${(idx % 3) * 0.1}s` }}
                >
                  <div className="sa-media sa-media--cover">
                    {idx < 3 && <span className="sa-badge card-badge">Ново</span>}
                    <ProductImage
                      src={product.img}
                      alt={product.name}
                      ratio={1}
                      sizes="(min-width: 992px) 380px, 50vw"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="sa-card-body">
                    <p className="sa-name">{product.name}</p>
                    <p className="sa-price">{formatPrice(product.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Link to="/catalog" className="sa-btn sa-btn--outline sa-btn--block home-view-all reveal-up">
              Виж всички продукти →
            </Link>
          </div>
        </section>
      )}

      {/* ── Как се ражда една приказка ── */}
      <div className="story-wrap">
        <Wave color="var(--sa-lav-100)" />
        <section className="story">
          <div className="sa-wrap">
            <div className="sa-section-head sa-section-head--center reveal-up">
              <span className="sa-section-kicker">от идея до подарък</span>
              <h2 className="sa-section-title">Как се ражда една приказка</h2>
            </div>

            <ol className="story-steps">
              {STEPS.map(({ n, title, text }, i) => (
                <li key={n} className="story-step reveal-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                  <span className="story-num">{n}</span>
                  <h3 className="story-step-title">{title}</h3>
                  <p className="story-step-text">{text}</p>
                  {i < STEPS.length - 1 && (
                    <span className="story-path" aria-hidden="true">
                      <Footprints size={26} color="var(--sa-rose-300)" />
                      <Footprints size={26} color="var(--sa-rose-200)" />
                      <Footprints size={26} color="var(--sa-rose-300)" />
                    </span>
                  )}
                </li>
              ))}
            </ol>

            <p className="story-note reveal-up">
              Пишете ни в чата — потвърждаваме размер, срок и доставка.
            </p>
            <div className="story-cta reveal-up">
              <button type="button" className="sa-btn sa-btn--primary" onClick={openChat}>
                <Icon path={ICON_CHAT} size={18} />
                Пиши ни
              </button>
            </div>
          </div>
        </section>
        <Wave color="var(--sa-paper)" />
      </div>

      {/* ── Бебета и деца ── */}
      {kidsProducts.length > 0 && (
        <section className="home-band">
          <div className="sa-wrap">
            <div className="kids-section">
              <Sparkle size={18} className="sa-doodle kids-star kids-star--1 sa-twinkle" />
              <Sparkle size={26} className="sa-doodle kids-star kids-star--2 sa-twinkle" color="var(--sa-rose-300)" />
              <Cloud width={110} className="sa-doodle kids-cloud sa-float sa-float--slow" />

              <div className="sa-section-head reveal-up">
                <div>
                  <span className="sa-section-kicker">за малките герои</span>
                  <h2 className="sa-section-title">Бебета и деца</h2>
                </div>
                <Link className="sa-section-link" to="/categories/detsa">Виж всички →</Link>
              </div>

              <div className="home-kids-grid">
                {kidsProducts.map((product, idx) => (
                  <Link
                    key={product.id}
                    to={`/catalog/${product.id}`}
                    className="sa-card reveal-up"
                    style={{ transitionDelay: `${(idx % 4) * 0.08}s` }}
                  >
                    <div className="sa-media sa-media--cover">
                      <ProductImage
                        src={product.img}
                        alt={product.name}
                        ratio={1}
                        sizes="(min-width: 992px) 280px, 50vw"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="sa-card-body">
                      <p className="sa-name">{product.name}</p>
                      <p className="sa-price">{formatPrice(product.price)}</p>
                    </div>
                  </Link>
                ))}
              </div>

              <div className="kids-foot">
                <Link className="sa-btn sa-btn--soft" to="/categories/detsa">
                  Виж всички продукти за деца →
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Виж как се прави ── */}
      {withVideos.length > 0 && (
        <section className="home-band">
          <div className="sa-wrap">
            <div className="sa-section-head reveal-up">
              <div>
                <span className="sa-section-kicker">под четката</span>
                <h2 className="sa-section-title">Виж как се прави</h2>
              </div>
            </div>
            <div className="home-video-grid">
              {withVideos.map((product, idx) => (
                <Link
                  key={product.id}
                  to={`/catalog/${product.id}`}
                  className="sa-card video-card reveal-up"
                  style={{ transitionDelay: `${(idx % 3) * 0.1}s` }}
                >
                  <div className="sa-media sa-media--portrait sa-media--cover video-media">
                    {/* poster = снимката на продукта: без нея картата стои като
                        тъмен правоъгълник, докато не минеш с мишката — а на
                        телефон „минаване с мишката“ няма изобщо. */}
                    <video
                      src={product.video}
                      poster={cdnImage(product.img, { w: 600, h: 750 })}
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      onMouseEnter={e => e.target.play()}
                      onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                    <span className="video-play" aria-hidden="true">
                      <Icon path={ICON_PLAY} size={14} fill />
                    </span>
                  </div>
                  <div className="sa-card-body">
                    <p className="sa-name">{product.name}</p>
                    <p className="sa-price">{formatPrice(product.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── От работната маса ──
          Снимките са в бели рамки като полароиди, накривени едва-едва. Така
          изглеждат нарочно подредени дори когато продуктът е сниман на бял
          фон — плътна лента от такива снимки би изглеждала празна. */}
      {bandProducts.length > 0 && (
        <section className="photo-band" aria-label="От работната маса">
          <Wave color="var(--sa-butter-100)" edge="top" />

          <div className="photo-band-inner">
            <Sparkle size={18} className="sa-doodle photo-band-star photo-band-star--1 sa-twinkle" />
            <Sparkle size={13} className="sa-doodle photo-band-star photo-band-star--2 sa-twinkle" color="var(--sa-rose-300)" />

            <div className="sa-wrap">
              <div className="sa-section-head sa-section-head--center reveal-up">
                <span className="sa-section-kicker">надникни в ателието</span>
                <h2 className="sa-section-title">От работната маса</h2>
              </div>

              <ul className="photo-strip sa-scroller">
                {bandProducts.map((product, i) => (
                  <li key={product.id} className={`photo-polaroid photo-polaroid--${i % 3}`}>
                    <Link to={`/catalog/${product.id}`} className="photo-polaroid-link">
                      <span className="photo-polaroid-media">
                        <ProductImage
                          src={product.img}
                          alt={product.name}
                          ratio={1}
                          widths={[240, 480]}
                          sizes="(min-width: 760px) 220px, 62vw"
                          loading="lazy"
                          decoding="async"
                        />
                      </span>
                      <span className="photo-polaroid-caption">{product.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Wave color="var(--sa-paper)" />
        </section>
      )}

      {/* ── Полезно за мама ── */}
      <section className="home-band home-band--pattern blog-band">
        <div className="sa-wrap">
          <div className="sa-section-head reveal-up">
            <div>
              <span className="sa-section-kicker">полезно за мама</span>
              <h2 className="sa-section-title">Преди празника</h2>
            </div>
            <Link to="/blog" className="sa-section-link">Всички статии →</Link>
          </div>
          <div className="mom-grid">
            {MOM_ARTICLES.map((key, idx) => {
              const article = ARTICLES[key];
              if (!article) return null;
              return (
                <Link
                  key={key}
                  to={`/blog/${key}`}
                  className="mom-card reveal-up"
                  style={{ transitionDelay: `${(idx % 3) * 0.1}s` }}
                >
                  <span className="mom-emoji" aria-hidden="true">{article.emoji}</span>
                  <span className="mom-tag">{article.tag}</span>
                  <h3 className="mom-title">{article.h1}</h3>
                  <p className="mom-text">{article.cardText}</p>
                  <span className="mom-foot">
                    <span className="mom-time">{article.readingTime}</span>
                    <span className="mom-more">Прочети →</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
